import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Stack } from "@mui/material";
import { useScrollSpy } from "../../hooks/useScrollSpy";
import { useTranslation } from "react-i18next";
import './subSectionNav.scss';

type Section = {
  id: string;
  label: string;
};

interface Props {
  sections: Section[];
  offset?: number;
}

function SubSectionNav({ sections, offset = 130 }: Props) {
  const { t } = useTranslation();
  const tabRefs = useRef<Record<string, HTMLSpanElement | null>>({});

  const sectionIds = useMemo(
    () => sections.map((s) => s.id),
    [sections]
  );

  const activeSection = useScrollSpy(sectionIds, offset);

  useEffect(() => {
    if (!activeSection) return;

    const el = tabRefs.current[activeSection];
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeSection]);

  useEffect(() => {
    const buttonId = "back-to-top";
    const hideBackToTop = () => {
      const btn = document.getElementById(buttonId);
      if (btn) {
        btn.style.display = "none";
      }
    };
    const handleScroll = () => {
      hideBackToTop();
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      const btn = document.getElementById(buttonId);
      if (btn) {
        btn.style.display = "";
      }
    };
  }, []);

  const handleScrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;

    const yOffset = -120;
    const y =
      section.getBoundingClientRect().top +
      window.pageYOffset +
      yOffset;

    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <nav className="pujaTabsNav-container">
      <Stack
        direction="row"
        spacing={5}
        className="pujaTabsScroll itemCenter"
      >
        {sections.map((sec) => (
          <span
            key={sec.id}
            ref={(el) => (tabRefs.current[sec.id] = el)}
            className={`pujaTab navPujaTab ${
              activeSection === sec.id ? "pujaTabActive" : ""
            }`}
            onClick={() => handleScrollToSection(sec.id)}
          >
            {t(sec.label)}
          </span>
        ))}
      </Stack>
    </nav>
  );
}

export default memo(SubSectionNav);
